import { Fragment, type ReactNode } from "react";
import { useT } from "../../lib/i18n";
import type { Version } from "../../lib/version";

const mdhm = (ms: number) => {
  const d = new Date(ms), p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

/** 碰过这一版的人：建草稿的、改过的、发布的。下拉的「只看我参与的」与筛选框都按它判 */
export function versionPeopleNames(v: Version): string[] {
  return [...new Set([v.user, v.publishedBy ?? "", v.updatedBy ?? "", ...(v.people ?? []).map((p) => p.actor)].filter(Boolean))];
}

/**
 * 版本上的人，一行小字（设计稿 engine.jsx VersionPeople）：「林岚 建 · 改过 sion、林岚 · sion 发布 08-12 18:40」。
 * 改过的人多于 max 个写「等 N 人」；名字可点（onPerson）：打开修改记录、范围「全部版本」、只看这个人。
 * 0256 之前没有发布日志的旧版本不写发布那段
 */
export function VersionPeople({ v, onPerson, max = 3 }: { v: Version; onPerson?: (name: string) => void; max?: number }) {
  const t = useT();
  const name = (n: string) => onPerson
    ? <button type="button" className="text-primary hover:underline" onClick={(e) => { e.stopPropagation(); onPerson(n); }}>{n}</button>
    : <span>{n}</span>;
  const edited = [...new Set([...(v.people ?? []).map((p) => p.actor), v.updatedBy ?? ""].filter(Boolean))];
  const parts: ReactNode[] = [];
  if (v.user) parts.push(<span key="c">{tpl(t("ver.people.created", { who: "\u0000" }), name(v.user))}</span>);
  if (edited.length) {
    const list = edited.slice(0, max).map((n, i) => <Fragment key={n}>{i ? "、" : ""}{name(n)}</Fragment>);
    parts.push(<span key="e">{tpl(t("ver.people.edited", { list: "\u0000" }), <>{list}</>)}{edited.length > max ? t("ver.people.more", { n: edited.length }) : ""}</span>);
  }
  if (v.publishedBy && v.publishedAtMs) {
    parts.push(<span key="p">{tpl(t("ver.people.published", { who: "\u0000", at: mdhm(v.publishedAtMs) }), name(v.publishedBy))}</span>);
  }
  if (!parts.length) return <span className="text-xs text-ink-placeholder">{t("auditChange.unknownActor")}</span>;
  return <span className="text-xs text-ink-secondary">{parts.map((x, i) => <Fragment key={i}>{i ? " · " : ""}{x}</Fragment>)}</span>;
}

/** 文案里的占位换成节点（人名要能点，不能拼成字符串） */
function tpl(text: string, node: ReactNode): ReactNode {
  const [a, b = ""] = text.split("\u0000");
  return <>{a}{node}{b}</>;
}
